"use client";
import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { ModalDialog } from "@/components/ui/ModalDialog";
import StatusGroup from "@/components/ui/StatusGroup";
import { AppTab } from "@/components/types";
import { useFeedbackForm } from "../hooks/useFeedbackForm";
import { FeedbackForm, feedbackFormId } from "./FeedbackForm";
import { useClarityAuth } from "@/lib/auth/use-clarity-auth";
import useSubmitFeedback from "../hooks/useSubmitFeedback";

interface FeedbackModalProps {
  open: boolean;
  onClose: VoidFunction;
  activeTab: AppTab;
}

export function FeedbackModal({
  open,
  onClose,
  activeTab,
}: FeedbackModalProps) {
  const { form, isSubmitDisabled } = useFeedbackForm(activeTab);
  const { user } = useClarityAuth();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleClose = () => {
    form.reset();
    setIsSubmitted(false);
    onClose();
  };

  const { handleSubmit, isLoading } = useSubmitFeedback({
    onSuccess: () => setIsSubmitted(true),
  });

  return (
    <ModalDialog
      open={open}
      isLoading={isLoading}
      onClose={handleClose}
      title="Send Feedback"
      description="Ask a question, report an issue, or share an idea. We'll review promptly."
      closeButtonDataTestId="feedback-modal-close"
      cancelButtonDataTestId="feedback-btn-cancel"
      confirmButtonDataTestId="feedback-btn-submit"
      hideFooter={isSubmitted}
      confirmButtonProps={{
        type: "submit",
        form: feedbackFormId,
        children: "Send Feedback",
        disabled: isSubmitDisabled,
        loading: isLoading,
      }}
      footerLeftSlot={
        <span className="text-[11px] text-[#6A7282]">
          Submitting as{" "}
          <span className="font-semibold text-[#364153]">{user?.email}</span>
        </span>
      }
    >
      {isSubmitted ? (
        <div className="py-8">
          <StatusGroup
            variant="success"
            title={
              <span className="text-md font-bold">Thanks for the feedback</span>
            }
            description={
              <span className="text-[#667085]">
                Our team will review it promptly.
              </span>
            }
          />
        </div>
      ) : (
        <FormProvider {...form}>
          <FeedbackForm onSubmit={form.handleSubmit(handleSubmit)} />
        </FormProvider>
      )}
    </ModalDialog>
  );
}
